import React, { useEffect, useState } from 'react';
import { Button, Form, Message, Segment } from 'semantic-ui-react';
import styled from 'styled-components';
import { IBillingDefaults } from '../../app/interfaces/IBilling';
import { describeDuration, formatMoney, parseMoneyToCents } from '../../utils/Billing';

/*
* The account-wide offer. Changing anything here changes it for every company
* that has not been quoted a price of its own, which is most of them - so the
* current offer is spelled out in words above the form.
*/
interface IBillingDefaultsFormProps {
    defaults: IBillingDefaults;
    saving: boolean;
    onSave: (update: Partial<IBillingDefaults>) => void;
}

const BillingDefaultsForm = ({ defaults, saving, onSave }: IBillingDefaultsFormProps) => {
    const [freeTrialDays, setFreeTrialDays] = useState(String(defaults.freeTrialDays));
    const [extensionDays, setExtensionDays] = useState(String(defaults.extensionDays));
    const [price, setPrice] = useState(formatMoney(defaults.extensionPriceCents, defaults.currency));
    const [contactEmail, setContactEmail] = useState(defaults.contactEmail);
    const [extensionsEnabled, setExtensionsEnabled] = useState(defaults.extensionsEnabled);
    const [invoiceDaysUntilDue, setInvoiceDaysUntilDue] = useState(String(defaults.invoiceDaysUntilDue));
    const [invoiceGraceDays, setInvoiceGraceDays] = useState(String(defaults.invoiceGraceDays));
    const [validationError, setValidationError] = useState<string | null>(null);

    // Re-seed the form whenever the saved defaults change, so a save (or
    // someone else's) is reflected rather than sitting behind stale input.
    useEffect(() => {
        setFreeTrialDays(String(defaults.freeTrialDays));
        setExtensionDays(String(defaults.extensionDays));
        setPrice(formatMoney(defaults.extensionPriceCents, defaults.currency));
        setContactEmail(defaults.contactEmail);
        setExtensionsEnabled(defaults.extensionsEnabled);
        setInvoiceDaysUntilDue(String(defaults.invoiceDaysUntilDue));
        setInvoiceGraceDays(String(defaults.invoiceGraceDays));
    }, [defaults]);

    const save = () => {
        const priceCents = parseMoneyToCents(price);
        const trialDays = parseInt(freeTrialDays, 10);
        const blockDays = parseInt(extensionDays, 10);
        const dueDays = parseInt(invoiceDaysUntilDue, 10);
        const graceDays = parseInt(invoiceGraceDays, 10);

        if (priceCents === null) {
            setValidationError('Enter the extension price as a number, for example 1000.');
            return;
        }
        if (Number.isNaN(trialDays) || trialDays < 0 || Number.isNaN(blockDays) || blockDays < 0) {
            setValidationError('Trial and extension lengths must be whole numbers of days.');
            return;
        }
        if (Number.isNaN(dueDays) || dueDays < 0 || Number.isNaN(graceDays) || graceDays < 0) {
            setValidationError('Invoice terms and grace must be whole numbers of days.');
            return;
        }

        setValidationError(null);
        onSave({
            freeTrialDays: trialDays,
            extensionDays: blockDays,
            extensionPriceCents: priceCents,
            contactEmail,
            extensionsEnabled,
            invoiceDaysUntilDue: dueDays,
            invoiceGraceDays: graceDays,
        });
    };

    return (
        <Segment>
            <h3>Default offer</h3>
            <StyledSummary>
                Every company without its own pricing gets{' '}
                <strong>{describeDuration(defaults.freeTrialDays)} free</strong>, then{' '}
                <strong>{formatMoney(defaults.extensionPriceCents, defaults.currency)}</strong>{' '}
                for <strong>{describeDuration(defaults.extensionDays)}</strong>.
            </StyledSummary>

            {validationError && <Message negative content={validationError} />}

            <Form>
                <Form.Group widths='equal'>
                    <Form.Input
                        label='Free trial (days)'
                        value={freeTrialDays}
                        onChange={(_, { value }) => setFreeTrialDays(value)}
                    />
                    <Form.Input
                        label='Extension length (days)'
                        value={extensionDays}
                        onChange={(_, { value }) => setExtensionDays(value)}
                    />
                    <Form.Input
                        label='Extension price'
                        value={price}
                        onChange={(_, { value }) => setPrice(value)}
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <Form.Input
                        label='Pricing contact'
                        value={contactEmail}
                        onChange={(_, { value }) => setContactEmail(value)}
                    />
                    <Form.Checkbox
                        label='Companies can buy extensions themselves'
                        checked={extensionsEnabled}
                        onChange={(_, { checked }) => setExtensionsEnabled(checked === true)}
                    />
                </Form.Group>
                <StyledSubheading>Annual invoicing</StyledSubheading>
                <StyledSummary>
                    Applies to companies past the trial. Each annual price is set per company
                    when the deal is struck - these are only the terms the invoice carries.
                </StyledSummary>
                <Form.Group widths='equal'>
                    <Form.Input
                        label='Payment terms (days)'
                        value={invoiceDaysUntilDue}
                        onChange={(_, { value }) => setInvoiceDaysUntilDue(value)}
                    />
                    <Form.Input
                        label='Grace after an invoice goes past due (days)'
                        value={invoiceGraceDays}
                        onChange={(_, { value }) => setInvoiceGraceDays(value)}
                    />
                </Form.Group>
                <Button primary loading={saving} disabled={saving} onClick={save}>
                    Save default offer
                </Button>
            </Form>
        </Segment>
    );
};

const StyledSubheading = styled('h4')`
    margin-bottom: 4px;
    margin-top: 20px;
`;

const StyledSummary = styled('p')`
    color: rgba(0, 0, 0, 0.7);
`;

export default BillingDefaultsForm;
